/**
 * Autoevaluación institucional multi-campus
 * (`/api/procesos-institucionales`).
 *
 * Agrupa una ejecución institucional POR CAMPUS y consolida sus resultados.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

/**
 * Autoevaluación institucional que se corre en varios campus.
 *
 * Cada campus ejecuta el mismo modelo por su cuenta —una `EjecucionModelo` por
 * sede— y el resultado de la universidad sale de consolidar los de todas.
 */

export interface CampusDisponible {
  id_campus: number
  nombre: string
  /** Cuántos programas aporta: es su peso en el consolidado ponderado. */
  programas: number
}

/** Forma con la que el API de proceso institucional devuelve este dato. */
export interface EjecucionDeCampus {
  id_ejecucion: number
  id_campus: number | null
  periodo: string
  estado: string
  campus?: { id_campus: number; nombre: string } | null
}

/** Forma con la que el API de proceso institucional devuelve este dato. */
export interface ProcesoInstitucional {
  id_proceso_institucional: number
  nombre: string
  periodo: string
  id_version: number
  id_tipo_ejecucion_modelo?: number | null
  criterio_consolidacion: 'simple' | 'ponderado'
  observaciones?: string | null
  ejecuciones?: EjecucionDeCampus[]
  version?: { id_version: number; nombre: string } | null
  tipo_proceso?: { tipo: string } | null
}

/** Forma con la que el API de proceso institucional devuelve este dato. */
export interface FactorConsolidado {
  ordinal: string | null
  nombre: string | null
  calificacion: number | null
  campus_con_nota: number
}

/** Forma con la que el API de proceso institucional devuelve este dato. */
export interface ConsolidadoInstitucional {
  escala_max: number
  global: number | null
  equivalencias: { nota: number | null; escala_max: number; en_cinco: number | null; en_diez: number | null }
  factores: FactorConsolidado[]
  campus_considerados: number
  /** Sedes que aún no tienen nota: se nombran para no dar por completo el consolidado. */
  campus_sin_calificar: Array<{ id_campus: number | null; campus: string | null }>
}

/** Forma con la que el API de proceso institucional devuelve este dato. */
export interface ResultadosInstitucionales {
  id_proceso_institucional: number
  nombre: string
  periodo: string
  criterio_consolidacion: string
  consolidado: ConsolidadoInstitucional | null
  campus: Array<{
    id_ejecucion: number
    id_campus: number | null
    campus: string | null
    peso: number
    // El árbol completo de esa sede, con la misma forma que
    // `GET /api/ejecuciones/{id}/resultados`.
    resultados: Record<string, any>
  }>
}

const BASE = '/api/procesos-institucionales'

/**
 * Lista proceso institucional · petición `GET`.
 *
 * @returns ProcesoInstitucional[]
 */
export async function listarProcesosInstitucionalesService(): Promise<ProcesoInstitucional[]> {
  const r = await http.get<ProcesoInstitucional[]>(BASE)
  return r.ok ? (r.data ?? []) : []
}

/**
 * Consulta proceso institucional · petición `GET`.
 *
 * @returns ProcesoInstitucional | null
 */
export async function getProcesoInstitucionalService(id: number): Promise<ProcesoInstitucional | null> {
  const r = await http.get<ProcesoInstitucional>(`${BASE}/${id}`)
  return r.ok ? (r.data ?? null) : null
}

/**
 * Consulta proceso institucional · petición `GET`.
 *
 * @returns CampusDisponible[]
 */
export async function getCampusDisponiblesService(): Promise<CampusDisponible[]> {
  const r = await http.get<CampusDisponible[]>(`${BASE}/campus-disponibles`)
  return r.ok ? (r.data ?? []) : []
}

/**
 * Crea proceso institucional · petición `POST`.
 *
 * @returns { ok: boolean; data?: ProcesoInstitucional; error?: string }
 */
export async function crearProcesoInstitucionalService(payload: {
  nombre: string
  periodo: string
  id_version: number
  id_tipo_ejecucion_modelo?: number | null
  criterio_consolidacion?: 'simple' | 'ponderado'
  observaciones?: string | null
  campus: number[]
}): Promise<{ ok: boolean; data?: ProcesoInstitucional; error?: string }> {
  const r = await http.post<ProcesoInstitucional>(BASE, payload)
  return r.ok
    ? { ok: true, data: r.data ?? undefined }
    : { ok: false, error: primerError(r, 'No se pudo crear el proceso institucional') }
}

/**
 * Actualiza proceso institucional · petición `PUT`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function actualizarProcesoInstitucionalService(
  id: number,
  payload: Partial<{ nombre: string; criterio_consolidacion: string; observaciones: string | null; campus: number[] }>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.put(`${BASE}/${id}`, payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo actualizar el proceso') }
}

/** Saca el campus del proceso; su ejecución y su trabajo se conservan. */
export async function quitarCampusDelProcesoService(id: number, idCampus: number): Promise<boolean> {
  const r = await http.delete(`${BASE}/${id}/campus/${idCampus}`)
  return r.ok
}

/**
 * Resultados del proceso.
 *
 * Sin `idCampus` devuelve el consolidado más el detalle de cada sede; con él,
 * solo esa sede (y `consolidado` viene null: un campus solo no es un
 * consolidado).
 */
export async function getResultadosInstitucionalesService(
  id: number,
  idCampus?: number | null
): Promise<ResultadosInstitucionales | null> {
  const query = idCampus ? `?id_campus=${idCampus}` : ''
  const r = await http.get<ResultadosInstitucionales>(`${BASE}/${id}/resultados${query}`)
  return r.ok ? (r.data ?? null) : null
}
